import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GpkgAutoStyleType } from '../handlers';

export type GpkgGeometryType = 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon' | 'GeometryCollection' | 'unknown';

export interface GpkgLayerInfo {
	name: string;
	type: 'feature' | 'tile';
	geometryType?: GpkgGeometryType;
	autoStyle?: GpkgAutoStyleType;
	selected: boolean;
}

@Component({
	selector: 'bp-gpkg-layer-select-modal',
	standalone: true,
	imports: [CommonModule, FormsModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Layer auswählen</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<p class="text-muted mb-3">{{ filename }}</p>

			@if (layers.length === 0) {
				<div class="alert alert-warning mb-0">
					<i class="bi bi-exclamation-triangle me-2"></i>Keine Layer in dieser GeoPackage-Datei gefunden.
				</div>
			} @else {
				<div class="mb-2 d-flex gap-2">
					<button type="button" class="btn btn-sm btn-outline-secondary" (click)="selectAll()">Alle auswählen</button>
					<button type="button" class="btn btn-sm btn-outline-secondary" (click)="selectNone()">Keine auswählen</button>
				</div>

				<div class="list-group">
					@for (layer of layers; track layer.name) {
						<label class="list-group-item list-group-item-action d-flex align-items-center">
							<input type="checkbox" class="form-check-input me-3" [(ngModel)]="layer.selected">
							<span class="flex-grow-1">{{ layer.name }}</span>
							@if (layer.autoStyle) {
								<span class="badge bg-light text-dark me-2">Autostyle</span>
							}
							<span class="badge" [class.bg-secondary]="layer.type === 'tile'"
							[class.bg-info]="layer.geometryType === 'Point' || layer.geometryType === 'MultiPoint'"
							[class.bg-warning]="layer.geometryType === 'LineString' || layer.geometryType === 'MultiLineString'"
							[class.bg-success]="layer.geometryType === 'Polygon' || layer.geometryType === 'MultiPolygon'"
							[class.bg-primary]="!layer.geometryType && layer.type === 'feature'">
								{{ getGeometryLabel(layer) }}
							</span>
						</label>
					}
				</div>
			}
		</div>
		<div class="modal-footer">
			<button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">Abbrechen</button>
			<button type="button" class="btn btn-primary" [disabled]="!hasSelection()" (click)="confirm()">
				<i class="bi bi-check-lg me-1"></i>Importieren
			</button>
		</div>
	`,
	styles: [`
		.list-group-item {
			cursor: pointer;
		}
		.list-group-item:hover {
			background-color: var(--bs-light);
		}
		.form-check-input {
			margin-top: 0;
		}
	`],
	
})
export class GpkgLayerSelectModalComponent {
	@Input() filename = '';
	@Input() layers: GpkgLayerInfo[] = [];

	constructor(public activeModal: NgbActiveModal) {}

	selectAll(): void {
		this.layers.forEach(l => l.selected = true);
	}

	selectNone(): void {
		this.layers.forEach(l => l.selected = false);
	}

	hasSelection(): boolean {
		return this.layers.some(l => l.selected);
	}

	confirm(): void {
		const selectedLayers = this.layers.filter(l => l.selected);
		this.activeModal.close(selectedLayers);
	}

	getGeometryLabel(layer: GpkgLayerInfo): string {
		if (layer.type === 'tile') return 'Tile';
		if (!layer.geometryType) return 'Feature';

		switch (layer.geometryType) {
			case 'Point':
			case 'MultiPoint':
				return 'Punkt';
			case 'LineString':
			case 'MultiLineString':
				return 'Linie';
			case 'Polygon':
			case 'MultiPolygon':
				return 'Polygon';
			case 'GeometryCollection':
				return 'Gemischt';
			default:
				return 'Feature';
		}
	}
}
